<template>
  <div
    class="customer-service-float"
    :class="{
      'at-edge': isAtEdge,
      'dragging': isDragging
    }"
    :style="{
      left: position.x + 'px',
      top: position.y + 'px'
    }"
    :title="$t('CustomerService.tooltip')"
    @mousedown="startDrag"
    @touchstart="startDrag"
    @touchend="handleTouchEnd"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @click="handleClick"
  >
    <div class="customer-service-icon">
      <i class="fa fa-comments"></i>
    </div>
    <div class="customer-service-text">{{ $t('CustomerService.title') }}</div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

export default {
  name: 'CustomerService',
  setup() {
    // 国际化
    const { t } = useI18n()

    // Vuex store
    const store = useStore()

    // 用户状态
    const isAuthenticated = computed(() => store.getters['user/isAuthenticated'])
    const currentUser = computed(() => store.getters['user/currentUser'])
    const userProfile = computed(() => store.getters['user/userProfile'])

    // 移动端检测
    const isMobile = computed(() => {
      return window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    })

    // 位置记忆键名
    const POSITION_STORAGE_KEY = 'customer-service-position'

    // 获取保存的位置
    const getSavedPosition = () => {
      try {
        const saved = localStorage.getItem(POSITION_STORAGE_KEY)
        return saved ? JSON.parse(saved) : null
      } catch (error) {
        console.warn('获取保存的客服图标位置失败:', error)
        return null
      }
    }

    // 保存位置
    const savePosition = (pos) => {
      try {
        localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify({
          x: pos.x,
          y: pos.y,
          timestamp: Date.now()
        }))
      } catch (error) {
        console.warn('保存客服图标位置失败:', error)
      }
    }

    // 位置状态 - 尝试从本地存储恢复位置
    const savedPos = getSavedPosition()
    const position = reactive({
      x: savedPos?.x ?? Math.max(0, window.innerWidth - 100),
      y: savedPos?.y ?? Math.max(0, window.innerHeight - 100)
    })
    
    // 拖动状态
    const isDragging = ref(false)
    const isAtEdge = ref(false)
    const dragOffset = reactive({ x: 0, y: 0 })
    const dragStartTime = ref(0)
    const dragStartPosition = reactive({ x: 0, y: 0 })
    const hasMoved = ref(false)
    
    // Crisp Chat 客服系统配置
    const crispWebsiteId = import.meta.env.VITE_CRISP_WEBSITE_ID
    const crispChatBaseUrl = import.meta.env.VITE_CRISP_CHAT_URL

    // 构建完整的客服链接
    const customerServiceUrl = ref('')

    // 检查 Crisp Chat 配置是否有效
    const isCrispConfigValid = computed(() => {
      return !!(crispWebsiteId &&
                crispChatBaseUrl &&
                crispWebsiteId.trim() !== '' &&
                crispChatBaseUrl.trim() !== '')
    })

    // 构建带用户信息的客服链接
    const buildCustomerServiceUrl = () => {
      // 如果配置无效，直接返回空字符串
      if (!isCrispConfigValid.value) {
        return ''
      }

      let url = `${crispChatBaseUrl}${crispWebsiteId}`

      // 如果用户已登录，添加用户信息参数
      if (isAuthenticated.value && currentUser.value) {
        const params = new URLSearchParams()

        // 添加用户基本信息
        if (currentUser.value.id) {
          params.append('user_id', currentUser.value.id.toString())
        }
        if (currentUser.value.username) {
          params.append('user_nickname', currentUser.value.username)
        }

        // 添加用户详细信息（如果有的话）
        if (userProfile.value) {
          if (userProfile.value.email) {
            params.append('user_email', userProfile.value.email)
          }
          if (userProfile.value.phone) {
            params.append('user_phone', userProfile.value.phone)
          }
        }

        // 添加会话标识
        params.append('session_id', `user_${currentUser.value.id}_${Date.now()}`)

        // 将参数添加到URL
        if (params.toString()) {
          url += (url.includes('?') ? '&' : '?') + params.toString()
        }
      }

      return url
    }

    // 初始化客服链接
    const initCustomerServiceUrl = () => {
      customerServiceUrl.value = buildCustomerServiceUrl()
    }

    /**
     * 打开客服系统
     */
    const openCustomerService = () => {
      // 检查配置是否有效
      if (!isCrispConfigValid.value) {
        return // 配置无效时直接返回，不执行任何操作
      }

      // 每次点击时重新构建URL，确保使用最新的用户信息
      const currentUrl = buildCustomerServiceUrl()

      // 如果没有URL，直接返回，不执行任何操作
      if (!currentUrl) {
        return
      }

      // 跳转到 Crisp Chat 客服系统
      try {
        // 使用 window.open 在新标签页中打开客服系统
        // 添加安全属性以符合现代浏览器要求
        const newWindow = window.open(
          currentUrl,
          '_blank',
          'noopener,noreferrer'
        )

        // 检查是否成功打开新窗口（防止弹窗被阻止）
        if (!newWindow) {
          console.warn('弹窗被浏览器阻止，请允许弹窗后重试')
        } else {
          // 成功打开，记录日志
          console.log('客服系统已在新标签页中打开', {
            authenticated: isAuthenticated.value,
            userId: currentUser.value?.id,
            url: currentUrl
          })
        }
      } catch (error) {
        console.error('打开客服系统失败:', error)
        // 降级方案：直接跳转到当前页面
        window.location.href = currentUrl
      }
    }
    
    /**
     * 检查是否在边界
     */
    const checkEdgePosition = () => {
      const threshold = 20 // 边界阈值
      const windowWidth = window.innerWidth
      const windowHeight = window.innerHeight
      const iconSize = 60 // 图标大小

      isAtEdge.value = (
        position.x <= threshold ||
        position.x >= windowWidth - iconSize - threshold ||
        position.y <= threshold ||
        position.y >= windowHeight - iconSize - threshold
      )

      // 设置边界隐藏的transform样式
      updateEdgeTransform()
    }

    /**
     * 更新边界隐藏的transform样式
     */
    const updateEdgeTransform = () => {
      if (!isAtEdge.value) return

      const threshold = 20
      const windowWidth = window.innerWidth
      const windowHeight = window.innerHeight
      const iconSize = 60

      let transform = ''

      // 左边界
      if (position.x <= threshold) {
        transform = 'translateX(-50%)'
      }
      // 右边界
      else if (position.x >= windowWidth - iconSize - threshold) {
        transform = 'translateX(50%)'
      }
      // 上边界
      else if (position.y <= threshold) {
        transform = 'translateY(-50%)'
      }
      // 下边界
      else if (position.y >= windowHeight - iconSize - threshold) {
        transform = 'translateY(50%)'
      }

      // 应用transform样式
      const element = document.querySelector('.customer-service-float')
      if (element) {
        element.style.transform = transform
      }
    }
    
    /**
     * 开始拖动
     */
    const startDrag = (e) => {
      // 鼠标事件：只响应左键
      if (e.type === 'mousedown' && e.button !== 0) return

      // 获取触摸点或鼠标位置
      const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX
      const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY

      // 记录拖拽开始信息
      dragStartTime.value = Date.now()
      dragStartPosition.x = clientX
      dragStartPosition.y = clientY
      hasMoved.value = false

      dragOffset.x = clientX - position.x
      dragOffset.y = clientY - position.y

      // 添加移动和结束事件监听器
      if (e.type === 'touchstart') {
        document.addEventListener('touchmove', onDrag, { passive: false })
        document.addEventListener('touchend', stopDrag)
      } else {
        document.addEventListener('mousemove', onDrag)
        document.addEventListener('mouseup', stopDrag)
      }

      // 防止文本选择和默认行为
      e.preventDefault()
    }
    
    /**
     * 拖动过程
     */
    const onDrag = (e) => {
      // 获取触摸点或鼠标位置
      const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX
      const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY

      // 检测是否真的移动了（移动距离超过阈值才算拖拽）
      const moveThreshold = 10 // 像素
      const deltaX = Math.abs(clientX - dragStartPosition.x)
      const deltaY = Math.abs(clientY - dragStartPosition.y)

      if (deltaX > moveThreshold || deltaY > moveThreshold) {
        hasMoved.value = true
        isDragging.value = true
      }

      // 只有真正开始拖拽时才移动位置
      if (isDragging.value && hasMoved.value) {
        const newX = clientX - dragOffset.x
        const newY = clientY - dragOffset.y

        // 限制在窗口范围内，允许拖动到边界进行隐藏效果
        const iconSize = 60
        const maxX = window.innerWidth - iconSize
        const maxY = window.innerHeight - iconSize

        position.x = Math.max(0, Math.min(newX, maxX))
        position.y = Math.max(0, Math.min(newY, maxY))

        checkEdgePosition()
      }

      // 防止页面滚动（仅对触摸事件）
      if (e.type === 'touchmove') {
        e.preventDefault()
      }
    }
    
    /**
     * 停止拖动
     */
    const stopDrag = () => {
      // 如果发生了移动，保存新位置
      if (hasMoved.value) {
        savePosition(position)
      }

      // 使用 setTimeout 确保在 click 事件之前重置状态
      setTimeout(() => {
        isDragging.value = false
        hasMoved.value = false
      }, 0)

      // 移除所有事件监听器
      document.removeEventListener('mousemove', onDrag)
      document.removeEventListener('mouseup', stopDrag)
      document.removeEventListener('touchmove', onDrag)
      document.removeEventListener('touchend', stopDrag)
    }

    /**
     * 处理触摸结束事件（移动端专用）
     */
    const handleTouchEnd = (e) => {
      // 如果没有移动，直接触发点击
      if (!hasMoved.value) {
        const clickDuration = Date.now() - dragStartTime.value
        if (clickDuration < 500) { // 快速触摸，触发点击
          // 阻止后续的 click 事件，避免重复触发
          e.preventDefault()

          // 直接调用客服系统打开逻辑
          openCustomerService()
        }
      }

      // 调用停止拖拽
      stopDrag()
    }
    
    /**
     * 鼠标进入
     */
    const handleMouseEnter = () => {
      // 鼠标悬停时显示完整图标，清除边界隐藏效果
      const element = document.querySelector('.customer-service-float')
      if (element && isAtEdge.value) {
        element.style.transform = 'scale(1.1)'
      }
    }

    /**
     * 鼠标离开
     */
    const handleMouseLeave = () => {
      // 鼠标离开时根据位置决定是否隐藏部分
      if (isAtEdge.value) {
        updateEdgeTransform()
      } else {
        const element = document.querySelector('.customer-service-float')
        if (element) {
          element.style.transform = ''
        }
      }
    }
    
    /**
     * 点击处理
     */
    const handleClick = (e) => {
      // 如果正在拖动或者已经移动过，不触发点击
      if (isDragging.value || hasMoved.value) {
        e.preventDefault()
        return
      }

      // 检查是否是快速点击（防止长按被误认为点击）
      const clickDuration = Date.now() - dragStartTime.value
      if (clickDuration > 500) { // 超过500ms认为是长按，不触发点击
        e.preventDefault()
        return
      }

      // 调用客服系统打开逻辑
      openCustomerService()
    }
    
    /**
     * 初始化位置
     */
    const initPosition = () => {
      const iconSize = 60
      // 移动端使用更小的边距，更贴近边界
      const margin = isMobile.value ? 15 : 40

      // 如果有保存的位置且在有效范围内，使用保存的位置
      const savedPos = getSavedPosition()
      if (savedPos) {
        const maxX = window.innerWidth - iconSize - margin
        const maxY = window.innerHeight - iconSize - margin

        // 验证保存的位置是否在当前屏幕范围内
        if (savedPos.x >= margin && savedPos.x <= maxX &&
            savedPos.y >= margin && savedPos.y <= maxY) {
          position.x = savedPos.x
          position.y = savedPos.y
          checkEdgePosition()
          return
        }
      }

      // 没有有效的保存位置，使用默认位置
      position.x = window.innerWidth - iconSize - margin
      position.y = window.innerHeight - iconSize - margin

      checkEdgePosition()
    }

    /**
     * 窗口大小变化处理
     */
    const handleResize = () => {
      const iconSize = 60
      const margin = isMobile.value ? 15 : 40

      // 检测是否是移动端键盘弹出导致的高度变化
      const isKeyboardOpen = isMobile.value && window.innerHeight < window.screen.height * 0.75

      if (isKeyboardOpen) {
        // 键盘弹出时，将图标固定在可视区域的右下角
        const visibleHeight = window.innerHeight
        const maxY = visibleHeight - iconSize - margin

        // 只调整Y坐标，保持X坐标不变
        position.y = Math.min(position.y, maxY)
      } else {
        // 正常的窗口大小变化处理
        const maxX = window.innerWidth - iconSize - margin
        const maxY = window.innerHeight - iconSize - margin

        // 限制位置在有效范围内
        position.x = Math.max(margin, Math.min(position.x, maxX))
        position.y = Math.max(margin, Math.min(position.y, maxY))
      }

      checkEdgePosition()
    }
    
    // 处理视口变化（主要用于移动端键盘检测）
    const handleVisualViewportChange = () => {
      if (window.visualViewport && isMobile.value) {
        const iconSize = 60
        const margin = 15
        const viewportHeight = window.visualViewport.height
        const maxY = viewportHeight - iconSize - margin

        // 确保图标在可视区域内
        if (position.y > maxY) {
          position.y = maxY
        }

        checkEdgePosition()
      }
    }

    // 生命周期
    onMounted(() => {
      // 初始化客服链接
      initCustomerServiceUrl()
      // 初始化位置
      initPosition()

      // 添加事件监听器
      window.addEventListener('resize', handleResize)

      // 移动端视口变化监听（用于键盘弹出检测）
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleVisualViewportChange)
      }
    })
    
    onUnmounted(() => {
      // 清理窗口事件监听器
      window.removeEventListener('resize', handleResize)

      // 清理视口事件监听器
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportChange)
      }

      // 清理拖拽事件监听器
      document.removeEventListener('mousemove', onDrag)
      document.removeEventListener('mouseup', stopDrag)
      document.removeEventListener('touchmove', onDrag)
      document.removeEventListener('touchend', stopDrag)
    })
    
    return {
      position,
      isDragging,
      isAtEdge,
      startDrag,
      handleTouchEnd,
      handleMouseEnter,
      handleMouseLeave,
      handleClick
    }
  }
}
</script>

<style scoped>
.customer-service-float {
  position: fixed;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  z-index: 9999;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}

.customer-service-float:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.2);
}

.customer-service-float.dragging {
  transition: none;
  transform: scale(1.05);
}

.customer-service-float.at-edge {
  transition: transform 0.3s ease;
}

.customer-service-icon {
  color: white;
  font-size: 24px;
  margin-bottom: 2px;
}

.customer-service-text {
  color: white;
  font-size: 10px;
  font-weight: 500;
  text-align: center;
  line-height: 1;
}

/* 移动端优化 */
@media (max-width: 768px) {
  .customer-service-float {
    width: 56px;
    height: 56px;
    /* 移动端触摸优化 */
    touch-action: manipulation;
  }

  .customer-service-icon {
    font-size: 22px;
  }

  .customer-service-text {
    font-size: 9px;
  }

  /* 移动端悬停效果调整 */
  .customer-service-float:hover {
    transform: none; /* 移动端不使用悬停缩放 */
  }

  .customer-service-float:active {
    transform: scale(0.95);
  }
}

/* 高DPI屏幕优化 */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .customer-service-float {
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  }
}

/* 深色模式支持 */
@media (prefers-color-scheme: dark) {
  .customer-service-float {
    background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }

  .customer-service-float:hover {
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.4);
  }
}

/* 减少动画效果（用户偏好） */
@media (prefers-reduced-motion: reduce) {
  .customer-service-float {
    transition: none;
  }

  .customer-service-float:hover {
    transform: none;
  }
}
</style>
